import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  from,
  map,
  Observable,
  of,
  retry,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { Job } from '../../interfaces/job.interface';
import { ERROR_PAGE_URL } from '../../pages/not-found-error/not-found-error.page';
import { JobService } from '../../services/job.service';
import { TemplateService } from '../../services/template.service';
import { handleHttpError } from '../../utils/api';
import { JobActions } from '../actions/job.actions';
import { selectSearchedParams } from '../selectors/job.selector';
import {
  selectActiveOrganisationId,
  selectUser,
} from '../selectors/user.selector';

@Injectable()
export class JobEffects {
  public get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.get),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([{ id }, organisation]) =>
        from(this.service.get(organisation, id)).pipe(
          map((job) => JobActions.getSuccess({ job })),
          catchError((error) => of(JobActions.getFailure({ error }))),
        ),
      ),
    ),
  );

  public getFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(JobActions.getFailure),
        tap(async () => await this.router.navigateByUrl(`/${ERROR_PAGE_URL}`)),
      ),
    { dispatch: false },
  );

  public search$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.search),
      withLatestFrom(
        this.store.select(selectActiveOrganisationId),
        this.store.select(selectUser),
      ),
      debounceTime(700),
      switchMap(([{ params }, organisation, { user }]) => {
        const queryParams = { ...params };

        if (user?.activeOrganisation.role === 'USER') {
          queryParams.user = user.id;
        }

        return from(this.service.search(organisation, queryParams)).pipe(
          map((response) =>
            JobActions.searchSuccess({
              jobs: response.items,
              pagination: {
                total: response.total,
                page: response.page || 1,
                limit: response.limit || 15,
                totalPages: response.totalPages || 0,
                hasNextPage: response.hasNextPage || false,
                hasPrevPage: response.hasPrevPage || false,
                nextPage: response.nextPage || null,
                prevPage: response.prevPage || null,
              },
            }),
          ),
          catchError((error) => of(JobActions.searchFailure({ error }))),
        );
      }),
    ),
  );

  public searchFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.searchFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.create),
      withLatestFrom(
        this.store.select(selectActiveOrganisationId),
        this.store.select(selectSearchedParams),
      ),
      switchMap(([action, organisationId, searchParams]) =>
        from(this.service.create(organisationId, action.payload)).pipe(
          retry(2),
          map((payload: Job) => {
            this.store.dispatch(JobActions.search({ params: searchParams }));

            return JobActions.createSuccess({ payload });
          }),
          catchError((error) =>
            of(
              JobActions.createFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public createFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.createFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.update),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.update(organisationId, action.jobId, action.payload),
        ).pipe(
          map((payload) => JobActions.updateSuccess({ payload })),
          catchError((error) =>
            of(
              JobActions.updateFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.updateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public createFromTemplate$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.createJobFromTemplate),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.templateService.createJob(organisationId, action.templateId),
        ).pipe(
          map((job) => JobActions.createJobFromTemplateSuccess({ job })),
          catchError((error) =>
            of(
              JobActions.createJobFromTemplateFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public createFromTemplateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.createJobFromTemplateFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public delete$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.delete),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.delete(organisationId, action.id),
        ).pipe(
          map((payload) => JobActions.deleteSuccess({ id : payload.id })),
          catchError((error) =>
            of(
              JobActions.deleteFailure({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public deleteFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.deleteFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public archive$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.archive),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.archive(organisationId, action.jobId, action.payload),
        ).pipe(
          map((payload) => JobActions.archiveSuccess({ payload })),
          catchError((error) => of(JobActions.archiveFailure({ error }))),
        ),
      ),
    ),
  );

  public archiveFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.archiveFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public updateNotes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.updateNotes),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(
          this.service.updateNotes(organisationId, action.jobId, action.payload),
        ).pipe(
          map((payload) => JobActions.updateNotesSuccess({ payload })),
          catchError((error) => of(JobActions.updateNotesFailure({ error }))),
        ),
      ),
    ),
  );

  public updateNotesFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.updateNotesFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public unarchive$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.unarchive),
      withLatestFrom(this.store.select(selectActiveOrganisationId)),
      switchMap(([action, organisationId]) =>
        from(this.service.unarchive(organisationId, action.jobId)).pipe(
          map((payload) => JobActions.unarchiveSuccess({ payload })),
          catchError((error) => of(JobActions.unarchiveFailure({ error }))),
        ),
      ),
    ),
  );

  public unarchiveFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobActions.unarchiveFailure),
      map((error) => handleHttpError(error)),
    ),
  );

  public constructor(
    private readonly actions$: Actions,
    private readonly service: JobService,
    private readonly templateService: TemplateService,
    private readonly router: Router,
    private readonly store: Store,
  ) {}
}
